// implementing vuex state management

import Vue from "vue";
import Vuex from "vuex";
import ProductCatalogProvider from "../providers/ProductCatalogProvider";
import LandingpageDataProvider from "../providers/LandingpageDataProvider";
import PriceRulesProvider from "../providers/PriceRulesProvider";
import MyStore from "./MyStore";
import ServerDataProvider from "../providers/ServerDataProvider";
import FeatureToggleProvider from "../providers/FeatureToggleProvider";
import ABTestingProvider from "../providers/ABTestingProvider";
import GlobalVersionProvider from "../providers/GlobalVersionProvider";
import UpsellRulesProvider from "../providers/UpsellRulesProvider";
import {flowSetting} from "../libraries/utils";
import * as mutationTypes from "./mutation-types";
import appConfig, {channelEnum} from "../appconfig";

import mutationTypesTid2 from "./tenantSpecificModules/storeModulesTid2/mutationTypesTid2";

import ServerLogProvider from "../providers/ServerLogProvider";

Vue.use(Vuex);

export let store = null;

/**
 * Returns the store to be used by this application.
 * As we have unittest errors if the store is initialized during module load we will initialize it lazily.
 * @returns {*}
 * @constructor
 */
const createStore = async () => {
	if (store) {
		switch(location.pathname) {
		case "/":
		case "/shop-selection":
			store.dispatch("resetGlobalSelectedShop");
			store.commit(mutationTypes.SET_TENANT_SPECIFIC_DATA, {
				key: "deliveryChoice"
			});
			break;
		case "/delivery-method":
			store.commit(mutationTypes.SET_TENANT_SPECIFIC_DATA, {
				key: "deliveryChoice"
			});
			break;
		}
		return store;
	} else {
		const storedUseApiCdnCaching = JSON.parse(localStorage.getItem("useApiCdnCaching"));
		const storedUseLandingpageEndpoint = JSON.parse(localStorage.getItem("useLandingpageEndpoint"));

		const [serverData, featureToggles, priceRules, shops, globalVersions, upsellRules, sallingHolidays, abTests] = await (async () => {
			// Have a fallback if the CDN caching is not working for some reason, so we can still pick up the fresh value
			// of the feature toggles (CDN caching may have been turned off but we can't pick it up due to the error)

			const getLoadPromise = (useCdnCache) => Promise.all([
				ServerDataProvider.getServerData(useCdnCache),
				FeatureToggleProvider.getFeatureTogglesAsPromise(!useCdnCache, useCdnCache),
				PriceRulesProvider.findAll({}, useCdnCache),
				undefined,
				GlobalVersionProvider.findAll(useCdnCache),
				UpsellRulesProvider.findAll({}, useCdnCache),
				undefined,
				ABTestingProvider.getABTests()
			]);

			const getLoadPromiseForLandingpage = (useCdnCache) => Promise.all([
				LandingpageDataProvider.getCombinedDataApiV1(useCdnCache),
				LandingpageDataProvider.getCombinedDataApiV2(useCdnCache),
				FeatureToggleProvider.getFeatureTogglesAsPromise(false, false, storedUseLandingpageEndpoint)
			]);
			// FeatureToggleProvider is added here to block redundant calls

			try {
				if (storedUseLandingpageEndpoint === true && !flowSetting.isFlow("admin")) {
					const [landingpageDataV1, landingpageDataV2] = await getLoadPromiseForLandingpage(storedUseApiCdnCaching);

					return [
						landingpageDataV1.monitoring,
						landingpageDataV1.featureToggles,
						landingpageDataV1.priceRules.filter(pr => {
							if(!pr.options) {
								return true;
							}
							const channel = appConfig.channelAlias();
							const isPriceRuleAvailableForChannel = channel && (
								!pr.options.channels
								|| !pr.options.channels.length
								|| pr.options.channels.includes(channel)
							);
							const deliveryChoice = store.getters.tenantSpecificData && store.getters.tenantSpecificData.deliveryChoice;
							const isPriceRuleAvailableForDeliveryMethod = (deliveryChoice || deliveryChoice === 0) && (
								!pr.options.deliveryMethods
								|| !pr.options.deliveryMethods.length
								|| pr.options.deliveryMethods.includes(deliveryChoice)
							);
							return isPriceRuleAvailableForChannel && isPriceRuleAvailableForDeliveryMethod;
						}),
						landingpageDataV1.shops,
						landingpageDataV2.globalVersions,
						landingpageDataV2.upsaleRules,
						landingpageDataV2.holidays,
						{}
					];
				}
				return await getLoadPromise(storedUseApiCdnCaching);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err);
				return getLoadPromise(false);
			}
		})();

		if(window.parent) window.parent.postMessage({ type: "SERVER_DATA", payload: { ...serverData, featureToggles } }, "*");

		const useNewProductCatalog = (featureToggles.useNewProductCatalog || {}).value || false;
		const useApiCdnCaching = (featureToggles.useApiCdnCaching || {}).value || false;
		const useNewShopsData = (featureToggles.useNewShopsData || {}).value || false;
		const useLandingpageEndpoint = (featureToggles.useLandingpageEndpoint || {}).value || false;

		localStorage.setItem("useApiCdnCaching", JSON.stringify(useApiCdnCaching));
		localStorage.setItem("useLandingpageEndpoint", JSON.stringify(useLandingpageEndpoint));

		// TODO - getting product catalog data on this level should be removed!!!
		let enrichedProductCatalogData = {
			products: [],
			categories: [],
			departments: []
		};

		if (!useNewShopsData || flowSetting.isFlow("admin")) {
			try {
				enrichedProductCatalogData = await ProductCatalogProvider
					.getProductCatalogData(null, useNewProductCatalog, useApiCdnCaching);
			} catch (e) {
				// TODO: do something about it
			}
		} else if (useNewProductCatalog) {
			const vuexStore = JSON.parse(localStorage.getItem("vuex"));
			if (vuexStore) {
				const shopStore = vuexStore ? vuexStore.shop : null;
				const shopState = shopStore ? shopStore.persistedData : null;
				const globalSelectedShop = shopState ? shopState.globalSelectedShop : null;
				const globalSelectedShopId = globalSelectedShop ? globalSelectedShop.id : null;
				const cartStore = vuexStore ? vuexStore.cart : null;
				const cartState = cartStore ? cartStore.persistedData : null;
				const cartData = (cartState && cartState.orderEditMode)
					? cartState.editModeData
					: cartState.newCartData;
				const tenantSpecificData = cartData ? cartData.tenantSpecificData : null;
				const deliveryChoice = tenantSpecificData ? tenantSpecificData.deliveryChoice : null;
				const isKiosk = appConfig.channelAlias() === channelEnum.kiosk;
				if ((isKiosk) || (!isKiosk && (!window.isNaN(deliveryChoice) || globalSelectedShopId))) {
					enrichedProductCatalogData = await ProductCatalogProvider
						.getProductCatalogData(null, useNewProductCatalog, useApiCdnCaching, globalSelectedShopId, deliveryChoice);
				}
			} else {
				// enrichedProductCatalogData = await ProductCatalogProvider
				// 	.getProductCatalogData(null, useNewProductCatalog, useApiCdnCaching);
			}
		}

		store = await MyStore({
			enrichedProductCatalogData,
			serverData,
			featureToggles,
			priceRules: priceRules.data.filter(pr => {
				if(!pr.options || !pr.options.channels || !pr.options.channels.length) {
					return true;
				}
				return pr.options.channels.includes(appConfig.channelAlias());
			}),
			globalVersions: globalVersions.data,
			upsellRulesData: upsellRules.data
		});
		await store.dispatch("cacheServerData", {});
		store.dispatch("startABTesting", abTests);

		if (storedUseLandingpageEndpoint === true && !flowSetting.isFlow("admin")) {
			// TODO Expiremental! Find the proper way to cache SG holidays and shops
			store.commit(mutationTypesTid2.SET_SG_HOLIDAYS, sallingHolidays);
			store.commit(mutationTypes.SET_CACHED_SHOPS, shops);
		}

		// TODO: remove this after the kiosk issues with customerDetails are fixed
		// https://sunset-boulevard-npd.monday.com/boards/4818172205/pulses/6955960717
		const channel = appConfig.channelAlias();
		if(channel === "kiosk") {	
			const getStackTrace = () => {
				const stack = (new Error().stack || "").split("\n").map(function (line) { return line.trim(); });
				return stack.splice(stack[0] === "Error" ? 2 : 1);
			};

			const original = store.commit.bind(store);
			store.commit = ((...args) => {
				const [ key, payload ] = args;
				if(["SET_USER_IDP_TOKEN", "CART_USER_DATA_SET", "USER_DATA_SET", "GATHER_USER_DATA"].includes(key)) {
					const logPayload = {
						storeCommitKey: key,
						storeCommitPayload: payload,
						userIdpToken: store.getters.userIdpToken,
						applicationUserIdpToken: store.getters.applicationUserIdpToken,
						customerDetails: store.getters.customerDetails,
						cartCustomerDetailsDebug: store.getters.cartCustomerDetailsDebug,
						getUserData: store.getters.getUserData,
						getUserDataDebug: store.getters.getUserDataDebug,
						kioskEnv: window.kioskHost,
						shopId: (store.getters.globalSelectedShop || {}).id,
						stacktrace: getStackTrace()
					};

					ServerLogProvider.postEvent({message: `Kiosk debug - deviceId: ${(window.kioskHost || {}).shopDeviceIdentifier} - commitKey: ${key}`, payload: logPayload});
				}
				
				original(...args);
			});
		}
		// -------------------------------------------------------------------------

		return store;
	}
};
export default createStore;
